// Permission types for the application

export type PermissionKey =
  // Dashboard
  | "dashboard.view"
  
  // Top Level Pages
  | "goods-in-out.view"
  | "goods-in-out.create"
  | "stock-assistant.view"
  | "quick-start.view"
  
  // Inventory Management
  | "inventory.stock-items.view"
  | "inventory.stock-items.create"
  | "inventory.stock-items.edit"
  | "inventory.stock-items.delete"
  | "inventory.non-stock-items.view"
  | "inventory.non-stock-items.create"
  | "inventory.non-stock-items.edit"
  | "inventory.non-stock-items.delete"
  | "inventory.stock-transfers.view"
  | "inventory.stock-transfers.create"
  | "inventory.stock-transfers.edit"
  | "inventory.stock-transfers.delete"
  | "inventory.movements.view"
  | "inventory.movements.create"
  | "inventory.movements.edit"
  | "inventory.movements.delete"
  | "inventory.tracking.view"
  | "inventory.tracking.edit"
  
  // Warehouse Operations
  | "warehouse.receive-goods.view"
  | "warehouse.receive-goods.create"
  | "warehouse.locations.view"
  | "warehouse.locations.create"
  | "warehouse.locations.edit"
  | "warehouse.locations.delete"
  | "warehouse.stock-takes.view"
  | "warehouse.stock-takes.create"
  | "warehouse.stock-takes.edit"
  | "warehouse.stock-takes.delete"
  
  // Purchasing
  | "purchasing.orders.view"
  | "purchasing.orders.create"
  | "purchasing.orders.edit"
  | "purchasing.orders.delete"
  | "purchasing.orders.approve"
  | "purchasing.suppliers.view"
  | "purchasing.suppliers.create"
  | "purchasing.suppliers.edit"
  | "purchasing.suppliers.delete"
  | "purchasing.suppliers.view-credentials"
  | "purchasing.suppliers.edit-credentials"
  
  // Sales
  | "sales.orders.view"
  | "sales.orders.create"
  | "sales.orders.edit"
  | "sales.orders.delete"
  | "sales.orders.approve"
  | "sales.customers.view"
  | "sales.customers.create"
  | "sales.customers.edit"
  | "sales.customers.delete"
  
  // Manufacturing
  | "manufacturing.boms.view"
  | "manufacturing.boms.create"
  | "manufacturing.boms.edit"
  | "manufacturing.boms.delete"
  | "manufacturing.work-orders.view"
  | "manufacturing.work-orders.create"
  | "manufacturing.work-orders.edit"
  | "manufacturing.work-orders.delete"
  
  // Projects & Cost Centers
  | "projects.view"
  | "projects.create"
  | "projects.edit"
  | "projects.delete"
  
  // Analytics & Forecasting
  | "analytics.forecasting.view"
  | "analytics.abc-classification.view"
  | "analytics.reorder.view"
  | "analytics.reorder.approve"
  | "analytics.margin-analysis.view"
  | "analytics.cost-analysis.view"
  
  // Pricing
  | "pricing.matrix.view"
  | "pricing.matrix.edit"
  | "pricing.advanced.view"
  | "pricing.advanced.edit"
  
  // Settings
  | "settings.system-config.view"
  | "settings.system-config.edit"
  | "settings.location-settings.view"
  | "settings.location-settings.edit"
  | "settings.integration.view"
  | "settings.integration.edit"
  | "settings.company.view"
  | "settings.company.edit"
  | "settings.user-management.view"
  | "settings.user-management.create"
  | "settings.user-management.edit"
  | "settings.user-management.delete"
  | "settings.custom-fields.view"
  | "settings.custom-fields.create"
  | "settings.custom-fields.edit"
  | "settings.custom-fields.delete";

export interface Permission {
  key: PermissionKey;
  label: string;
  description: string;
  category: string;
}

export interface UserProfile {
  id: string;
  name: string;
  description: string;
  permissions: PermissionKey[];
  isDefault: boolean;
  isSystem: boolean; // System profiles cannot be deleted
  createdAt: string;
  updatedAt: string;
}

export interface User {
  id: string;
  name: string;
  email: string;
  profileId: string;
  isActive: boolean;
  createdAt: string;
  lastLogin?: string;
}

export const PERMISSION_CATEGORIES = [
  { id: "dashboard", label: "Dashboard" },
  { id: "top-level", label: "Quick Access" },
  { id: "inventory", label: "Inventory Management" },
  { id: "warehouse", label: "Warehouse Operations" },
  { id: "purchasing", label: "Purchasing" },
  { id: "sales", label: "Sales" },
  { id: "manufacturing", label: "Manufacturing" },
  { id: "projects", label: "Projects & Cost Centers" },
  { id: "analytics", label: "Analytics & Forecasting" },
  { id: "pricing", label: "Pricing" },
  { id: "settings", label: "Settings" },
] as const;

export const ALL_PERMISSIONS: Permission[] = [
  // Dashboard
  { key: "dashboard.view", label: "View Dashboard", description: "Access to main dashboard", category: "dashboard" },
  
  // Top Level Pages
  { key: "goods-in-out.view", label: "View Goods In / Out", description: "Access goods in/out page", category: "top-level" },
  { key: "goods-in-out.create", label: "Create Goods Transactions", description: "Create goods in/out transactions", category: "top-level" },
  { key: "stock-assistant.view", label: "View Stock Assistant", description: "Access AI stock assistant", category: "top-level" },
  { key: "quick-start.view", label: "View Quick Start", description: "Access quick start guide", category: "top-level" },
  
  // Inventory Management
  { key: "inventory.stock-items.view", label: "View Stock Items", description: "View stock items list", category: "inventory" },
  { key: "inventory.stock-items.create", label: "Create Stock Items", description: "Create new stock items", category: "inventory" },
  { key: "inventory.stock-items.edit", label: "Edit Stock Items", description: "Edit existing stock items", category: "inventory" },
  { key: "inventory.stock-items.delete", label: "Delete Stock Items", description: "Delete stock items", category: "inventory" },
  { key: "inventory.non-stock-items.view", label: "View Non-Stock Items", description: "View non-stock items", category: "inventory" },
  { key: "inventory.non-stock-items.create", label: "Create Non-Stock Items", description: "Create non-stock items", category: "inventory" },
  { key: "inventory.non-stock-items.edit", label: "Edit Non-Stock Items", description: "Edit non-stock items", category: "inventory" },
  { key: "inventory.non-stock-items.delete", label: "Delete Non-Stock Items", description: "Delete non-stock items", category: "inventory" },
  { key: "inventory.stock-transfers.view", label: "View Stock Transfers", description: "View stock transfers", category: "inventory" },
  { key: "inventory.stock-transfers.create", label: "Create Stock Transfers", description: "Create stock transfers", category: "inventory" },
  { key: "inventory.stock-transfers.edit", label: "Edit Stock Transfers", description: "Edit stock transfers", category: "inventory" },
  { key: "inventory.stock-transfers.delete", label: "Delete Stock Transfers", description: "Delete stock transfers", category: "inventory" },
  { key: "inventory.movements.view", label: "View Stock Movements", description: "View stock movement history", category: "inventory" },
  { key: "inventory.movements.create", label: "Create Stock Movements", description: "Create stock movements", category: "inventory" },
  { key: "inventory.movements.edit", label: "Edit Stock Movements", description: "Edit stock movements", category: "inventory" },
  { key: "inventory.movements.delete", label: "Delete Stock Movements", description: "Delete stock movements", category: "inventory" },
  { key: "inventory.tracking.view", label: "View Lot/Batch/Serial", description: "View tracking information", category: "inventory" },
  { key: "inventory.tracking.edit", label: "Edit Lot/Batch/Serial", description: "Edit tracking information", category: "inventory" },
  
  // Warehouse Operations
  { key: "warehouse.receive-goods.view", label: "View Receive Goods", description: "View goods receipt", category: "warehouse" },
  { key: "warehouse.receive-goods.create", label: "Create Receive Goods", description: "Create goods receipt", category: "warehouse" },
  { key: "warehouse.locations.view", label: "View Locations", description: "View warehouses and locations", category: "warehouse" },
  { key: "warehouse.locations.create", label: "Create Locations", description: "Create new warehouses/locations", category: "warehouse" },
  { key: "warehouse.locations.edit", label: "Edit Locations", description: "Edit warehouses/locations", category: "warehouse" },
  { key: "warehouse.locations.delete", label: "Delete Locations", description: "Delete warehouses/locations", category: "warehouse" },
  { key: "warehouse.stock-takes.view", label: "View Stock Takes", description: "View stock takes", category: "warehouse" },
  { key: "warehouse.stock-takes.create", label: "Create Stock Takes", description: "Create new stock takes", category: "warehouse" },
  { key: "warehouse.stock-takes.edit", label: "Edit Stock Takes", description: "Edit stock takes", category: "warehouse" },
  { key: "warehouse.stock-takes.delete", label: "Delete Stock Takes", description: "Delete stock takes", category: "warehouse" },
  
  // Purchasing
  { key: "purchasing.orders.view", label: "View Purchase Orders", description: "View purchase orders", category: "purchasing" },
  { key: "purchasing.orders.create", label: "Create Purchase Orders", description: "Create new purchase orders", category: "purchasing" },
  { key: "purchasing.orders.edit", label: "Edit Purchase Orders", description: "Edit purchase orders", category: "purchasing" },
  { key: "purchasing.orders.delete", label: "Delete Purchase Orders", description: "Delete purchase orders", category: "purchasing" },
  { key: "purchasing.orders.approve", label: "Approve Purchase Orders", description: "Approve purchase orders", category: "purchasing" },
  { key: "purchasing.suppliers.view", label: "View Suppliers", description: "View suppliers list", category: "purchasing" },
  { key: "purchasing.suppliers.create", label: "Create Suppliers", description: "Create new suppliers", category: "purchasing" },
  { key: "purchasing.suppliers.edit", label: "Edit Suppliers", description: "Edit suppliers", category: "purchasing" },
  { key: "purchasing.suppliers.delete", label: "Delete Suppliers", description: "Delete suppliers", category: "purchasing" },
  { key: "purchasing.suppliers.view-credentials", label: "View Supplier Credentials", description: "View supplier portal login credentials", category: "purchasing" },
  { key: "purchasing.suppliers.edit-credentials", label: "Edit Supplier Credentials", description: "Add/edit supplier portal login credentials", category: "purchasing" },
  
  // Sales
  { key: "sales.orders.view", label: "View Sales Orders", description: "View sales orders", category: "sales" },
  { key: "sales.orders.create", label: "Create Sales Orders", description: "Create new sales orders", category: "sales" },
  { key: "sales.orders.edit", label: "Edit Sales Orders", description: "Edit sales orders", category: "sales" },
  { key: "sales.orders.delete", label: "Delete Sales Orders", description: "Delete sales orders", category: "sales" },
  { key: "sales.orders.approve", label: "Approve Sales Orders", description: "Approve sales orders", category: "sales" },
  { key: "sales.customers.view", label: "View Customers", description: "View customers list", category: "sales" },
  { key: "sales.customers.create", label: "Create Customers", description: "Create new customers", category: "sales" },
  { key: "sales.customers.edit", label: "Edit Customers", description: "Edit customers", category: "sales" },
  { key: "sales.customers.delete", label: "Delete Customers", description: "Delete customers", category: "sales" },
  
  // Manufacturing
  { key: "manufacturing.boms.view", label: "View BOMs", description: "View bills of material", category: "manufacturing" },
  { key: "manufacturing.boms.create", label: "Create BOMs", description: "Create new BOMs", category: "manufacturing" },
  { key: "manufacturing.boms.edit", label: "Edit BOMs", description: "Edit BOMs", category: "manufacturing" },
  { key: "manufacturing.boms.delete", label: "Delete BOMs", description: "Delete BOMs", category: "manufacturing" },
  { key: "manufacturing.work-orders.view", label: "View Work Orders", description: "View work orders", category: "manufacturing" },
  { key: "manufacturing.work-orders.create", label: "Create Work Orders", description: "Create new work orders", category: "manufacturing" },
  { key: "manufacturing.work-orders.edit", label: "Edit Work Orders", description: "Edit work orders", category: "manufacturing" },
  { key: "manufacturing.work-orders.delete", label: "Delete Work Orders", description: "Delete work orders", category: "manufacturing" },
  
  // Projects & Cost Centers
  { key: "projects.view", label: "View Projects & Cost Centers", description: "View projects and cost centers", category: "projects" },
  { key: "projects.create", label: "Create Projects & Cost Centers", description: "Create new projects and cost centers", category: "projects" },
  { key: "projects.edit", label: "Edit Projects & Cost Centers", description: "Edit projects and cost centers", category: "projects" },
  { key: "projects.delete", label: "Delete Projects & Cost Centers", description: "Delete projects and cost centers", category: "projects" },
  
  // Analytics & Forecasting
  { key: "analytics.forecasting.view", label: "View Demand Forecasting", description: "View AI demand forecasting", category: "analytics" },
  { key: "analytics.abc-classification.view", label: "View ABC Classification", description: "View ABC classification", category: "analytics" },
  { key: "analytics.reorder.view", label: "View Reorder Recommendations", description: "View reorder recommendations", category: "analytics" },
  { key: "analytics.reorder.approve", label: "Approve Reorder Recommendations", description: "Approve reorder recommendations", category: "analytics" },
  { key: "analytics.margin-analysis.view", label: "View Margin Analysis", description: "View margin analysis", category: "analytics" },
  { key: "analytics.cost-analysis.view", label: "View Cost Analysis", description: "View cost analysis", category: "analytics" },
  
  // Pricing
  { key: "pricing.matrix.view", label: "View Price Matrix", description: "View price matrix", category: "pricing" },
  { key: "pricing.matrix.edit", label: "Edit Price Matrix", description: "Edit price matrix", category: "pricing" },
  { key: "pricing.advanced.view", label: "View Advanced Pricing", description: "View advanced pricing rules", category: "pricing" },
  { key: "pricing.advanced.edit", label: "Edit Advanced Pricing", description: "Edit advanced pricing rules", category: "pricing" },
  
  // Settings
  { key: "settings.system-config.view", label: "View System Configuration", description: "View system configuration", category: "settings" },
  { key: "settings.system-config.edit", label: "Edit System Configuration", description: "Edit system configuration", category: "settings" },
  { key: "settings.location-settings.view", label: "View Location Settings", description: "View location settings", category: "settings" },
  { key: "settings.location-settings.edit", label: "Edit Location Settings", description: "Edit location settings", category: "settings" },
  { key: "settings.integration.view", label: "View Integration", description: "View integration settings", category: "settings" },
  { key: "settings.integration.edit", label: "Edit Integration", description: "Edit integration settings", category: "settings" },
  { key: "settings.company.view", label: "View Company Settings", description: "View company information", category: "settings" },
  { key: "settings.company.edit", label: "Edit Company Settings", description: "Edit company information", category: "settings" },
  { key: "settings.user-management.view", label: "View User Management", description: "View users and profiles", category: "settings" },
  { key: "settings.user-management.create", label: "Create Users & Profiles", description: "Create new users and profiles", category: "settings" },
  { key: "settings.user-management.edit", label: "Edit Users & Profiles", description: "Edit users and profiles", category: "settings" },
  { key: "settings.user-management.delete", label: "Delete Users & Profiles", description: "Delete users and profiles", category: "settings" },
  { key: "settings.custom-fields.view", label: "View Custom Fields", description: "View custom field definitions", category: "settings" },
  { key: "settings.custom-fields.create", label: "Create Custom Fields", description: "Create new custom fields", category: "settings" },
  { key: "settings.custom-fields.edit", label: "Edit Custom Fields", description: "Edit custom fields", category: "settings" },
  { key: "settings.custom-fields.delete", label: "Delete Custom Fields", description: "Delete custom fields", category: "settings" },
];
